import React, { useContext, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useCurrency } from '@/hooks/useCurrency';
import { useClientSalesReport } from '@/hooks/useClientSalesReport';
import { ClientSalesReportTable } from './ClientSalesReportTable';
import CustomRangePicker from '@/components/ui/RangePicker/RangePicker';
import { TrendingUp, Receipt, DollarSign, Download, BarChart3 } from 'lucide-react';

interface ClientSalesReportModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClientSalesReportModal: React.FC<ClientSalesReportModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const { formatAmount } = useCurrency();

  const [from, setFrom] = useState<Date>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [to, setTo] = useState<Date>(() => new Date());

  // Ensure valid date range: swap if from > to
  const validFrom = from <= to ? from : to;
  const validTo = from <= to ? to : from;

  const { clientSalesReport, isLoading, error } = useClientSalesReport(validFrom, validTo);

  // Calculate summary metrics
  const totalRevenue = clientSalesReport.reduce((acc, curr) => acc + curr.totalSales, 0);
  const totalTransactions = clientSalesReport.reduce((acc, curr) => acc + curr.transactionCount, 0);
  const averageTicket = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-6 overflow-hidden">
        <DialogHeader className="space-y-1.5 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-foreground">
                  {translator('Clients.salesReportTitle') || 'Saved Client Sales Report'}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {translator('Clients.salesReportDesc') || 'Analyze sales performance and purchase history for your saved clients.'}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Filters and Date Range */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-4 border-b border-border bg-muted/20 px-4 rounded-lg my-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            <span className="text-sm font-medium text-muted-foreground shrink-0">
              {translator('Clients.dateRange') || 'Date Range:'}
            </span>
            <CustomRangePicker
              from={from}
              to={to}
              setFrom={setFrom}
              setTo={setTo}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-2">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between shadow-xs">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {translator('Clients.totalRevenue') || 'Total Revenue'}
              </p>
              <h3 className="text-xl font-bold text-foreground mt-1">
                {formatAmount(totalRevenue)}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between shadow-xs">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {translator('Clients.totalTransactions') || 'Total Transactions'}
              </p>
              <h3 className="text-xl font-bold text-foreground mt-1">
                {totalTransactions}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <Receipt className="h-5 w-5" />
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between shadow-xs">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {translator('Clients.averageTicket') || 'Average Ticket'}
              </p>
              <h3 className="text-xl font-bold text-foreground mt-1">
                {formatAmount(averageTicket)}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-purple-500/10 text-purple-600 flex items-center justify-center">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[450px] pr-1">
          <ClientSalesReportTable
            data={clientSalesReport}
            isLoading={isLoading}
            error={error}
          />
        </div>

        {/* Footer Actions */}
        <div className="pt-4 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translator('Global.close') || 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientSalesReportModal;
